import { Feature, Map } from 'ol';
import { Point } from 'ol/geom';
import VectorSource from 'ol/source/Vector';
import {
  action,
  computed,
  makeObservable,
  observable,
  runInAction,
} from 'mobx';
import Style from 'ol/style/Style';
import Icon from 'ol/style/Icon';
import image from '@assets/images/events-layer.svg';
import { addBadgeStyle, BadgeOptions } from '@utils/badge';
import ClusterSource from 'ol/source/Cluster';
import { bbox } from 'ol/loadingstrategy';
import VectorLayer from 'ol/layer/Vector';
import {
  IEventsGeometry,
  EventsId,
  IEventsItem,
  EventsLayerQuery,
} from './types';
import { AnimatedClusterLayerBuilder } from '@datum-group/datum-map/lib';
import { ISelectCluster } from '@datum-map/Layer/AnimatedClusterLayerBuilder/types';
import { DATUM_MAP_CONFIG } from '../datumMapConfig';
import {
  FeatureCollectionSourceLoaderDecorator,
  LoadFeatureCollectionCallback,
} from '../SourceLoaderDecorator';

export type Mode = 'editing' | 'viewing';

interface EventsLayerOptions {
  mode: Mode;
}

export class EventsLayer {
  @observable private _isVisible = false;
  @observable private _mode: Mode;
  @observable private _count = undefined;
  @observable private _requestsInProgress: number = 0;
  @observable private _error: string | undefined = undefined;

  private _query?: EventsLayerQuery;
  private _source: VectorSource<Feature<Point>> = new VectorSource({
    strategy: bbox,
  });

  private _emptyStyle = new Style({});
  private _singleFeatureStyle = new Style({
    image: new Icon({ src: image, anchor: [0.5, 0.5], scale: 0.03 }),
  });
  private _clusterStyle = new Style({
    image: new Icon({ src: image, anchor: [0.5, 0.5], scale: 0.04 }),
  });
  private _badgeOptions: BadgeOptions = {
    displacement: [15, 15],
    backgroundColor: 'rgb(64, 152, 87)',
    color: '#fff',
  };
  private _zIndex = 999;

  private _animatedLayer: VectorLayer;
  private _animatedLayerSelect: ISelectCluster | undefined = undefined;
  private _defaultLayer: VectorLayer;

  constructor(
    private _loadFeatures: LoadFeatureCollectionCallback,
    private _map: Map,
    { mode }: EventsLayerOptions,
  ) {
    const builder = new AnimatedClusterLayerBuilder(
      DATUM_MAP_CONFIG.map.layer.cluster,
    );

    const { layer, select } = builder
      .setMap(this._map)
      .setSource(new ClusterSource({ source: this._source, distance: 120 }))
      .setStyle({
        clusterStyle: addBadgeStyle(this._clusterStyle, this._badgeOptions),
        featureStyle: [this._singleFeatureStyle],
        activeClusterStyle: this._emptyStyle,
        activeFeatureStyle: this._singleFeatureStyle,
      })
      .build();

    this._animatedLayer = layer;

    new FeatureCollectionSourceLoaderDecorator(
      this._source,
      this._map,
      this._loadFeatures,
    );

    if (select) {
      this._animatedLayerSelect = select;
    }

    this._animatedLayer.setZIndex(this._zIndex);

    this._defaultLayer = new VectorLayer({ source: this._source });
    this._defaultLayer.setZIndex(this._zIndex);
    this._defaultLayer.setStyle(this._singleFeatureStyle);
    this._mode = mode;

    this._bind();

    makeObservable<EventsLayer>(this);
  }

  @computed get source(): VectorSource<Feature<Point>> {
    return this._source;
  }

  @computed get isVisible(): boolean {
    return this._isVisible;
  }

  @computed get mode() {
    return this._mode;
  }

  set mode(value: Mode) {
    this._mode = value;

    this._setLayer();
  }

  @computed get layer(): VectorLayer {
    if (this._mode === 'editing') {
      return this._defaultLayer;
    }

    if (this._mode === 'viewing') {
      return this._animatedLayer;
    }

    return this._animatedLayer;
  }

  // TODO: Всегда равен 0. Эндпоинт open-news-geojson/ не возврашает count
  @computed get count() {
    return this._count;
  }

  get query() {
    return this._query;
  }

  set query(query) {
    this._query = query;
  }

  // TODO: Всегда равно undefined.
  // Надо договориться об отображении ошибки, в том числе при условии частого изменения bbox. (10 запросов подряд => 9 первых кенселится => интерфейс моргает)
  @computed get error(): string | undefined {
    return this._error;
  }

  @computed get isLoading(): boolean {
    return this._requestsInProgress > 0;
  }

  getFeatureById = (id: number | string) => {
    return this._source.getFeatureById(id);
  };

  hasFeature = (feature: Feature<Point>) => {
    return this._source.hasFeature(feature);
  };

  addFeatures = (value: IEventsItem[]) => {
    const features = value.map((v) => this._serializeEventsItem(v));
    this._source.addFeatures(features);
  };

  addFeature = (value: IEventsItem) => {
    const feature = this._serializeEventsItem(value);
    this._source.addFeature(feature);
  };

  removeFeature = (id: EventsId) => {
    const feature = this._source.getFeatureById(id);

    if (feature) this._source.removeFeature(feature);
  };

  clearFeatures = () => {
    this._source.clear();
  };

  editFeature = (value: IEventsItem) => {
    const feature = this._source.getFeatureById(value.id);

    if (!feature) this.addFeature(value);
    else {
      feature.setProperties(value.properties);

      if (!value.geometry) feature.setGeometry(undefined);
      else
        feature.setGeometry(this._serializeEventsItemGeometry(value.geometry));
    }
  };

  @action show = () => {
    if (!this._isVisible) {
      this._map.addLayer(this.layer);
    }

    this._isVisible = true;
  };

  @action hide = () => {
    this._isVisible = false;
    this._map.removeLayer(this.layer);
  };

  private _serializeEventsItem = (value: IEventsItem): Feature<Point> => {
    const coordinates = value.geometry?.coordinates;

    const feature = new Feature<Point>({
      geometry: coordinates ? new Point(coordinates) : undefined,
    });

    feature.setId(value.id);
    feature.setProperties(value.properties);

    return feature;
  };

  private _serializeEventsItemGeometry = (value: IEventsGeometry): Point => {
    return new Point(value.coordinates);
  };

  private _setLayer = () => {
    // Закрывает кластер перед сменой слоя. Иначе раскрывшийся кластер продолжает отображаться даже при несуществующем слое
    this._animatedLayerSelect?.clear();

    if (this._mode === 'editing') {
      this._map.removeLayer(this._animatedLayer);
      this._map.addLayer(this._defaultLayer);
    }

    if (this._mode === 'viewing') {
      this._map.removeLayer(this._defaultLayer);
      this._map.addLayer(this._animatedLayer);
    }
  };

  private _bind = () => {
    this.source.on('featuresloadstart', () => {
      runInAction(() => this._requestsInProgress++);
    });

    this.source.on(['featuresloadend', 'featuresloaderror'], () => {
      runInAction(() => this._requestsInProgress--);
    });
  };
}
